import React, { useEffect, useState, useCallback } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { contractsService } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import { Icon } from '@iconify/react/dist/iconify.js';
import 'react-toastify/dist/ReactToastify.css';

const ContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [selectedContract, setSelectedContract] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    version: '1.0',
    is_active: true,
    requires_acceptance: true,
    contract_type: '',
    for_invoice_partners: null
  });

  const loadContracts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const response = await contractsService.getAllContracts(params);

      if (response.success) {
        setContracts(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Sözleşmeler yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading contracts:', error);
      toast.error('Sözleşmeler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleEdit = (contract) => {
    setSelectedContract(contract);
    setFormData({
      title: contract.title,
      content: contract.content,
      version: contract.version,
      is_active: contract.is_active,
      requires_acceptance: contract.requires_acceptance,
      contract_type: contract.contract_type || '',
      for_invoice_partners: contract.for_invoice_partners
    });
    setShowEditModal(true);
  };

  const handleCreate = () => {
    setSelectedContract(null);
    setFormData({
      title: '',
      content: '',
      version: '1.0',
      is_active: true,
      requires_acceptance: true,
      contract_type: '',
      for_invoice_partners: null
    });
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    try {
      let response;
      if (selectedContract) {
        response = await contractsService.updateContract(selectedContract.id, formData);
      } else {
        response = await contractsService.createContract(formData);
      }

      if (response.success) {
        toast.success(selectedContract ? 'Sözleşme güncellendi' : 'Sözleşme oluşturuldu');
        setShowEditModal(false);
        setShowCreateModal(false);
        setSelectedContract(null);
        loadContracts();
      } else {
        toast.error(response.error || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Error saving contract:', error);
      toast.error('İşlem sırasında bir hata oluştu');
    }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('Bu sözleşmeyi tamamen silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }

    const response = await contractsService.deleteContract(contractId);
    if (response.success) {
      toast.success('Sözleşme silindi');
      loadContracts();
    } else {
      toast.error(response.error || 'Sözleşme silinemedi');
    }
  };

  const handleToggleStatus = async (contractId, currentStatus) => {
    const response = await contractsService.toggleContractStatus(contractId);
    if (response.success) {
      toast.success(response.message || `Sözleşme ${currentStatus ? 'pasif' : 'aktif'} yapıldı`);
      loadContracts();
    } else {
      toast.error(response.error || 'Sözleşme durumu değiştirilemedi');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <MasterLayout>
      <ToastContainer />
      <Breadcrumb title="Partner Sözleşmeleri" />
      
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">Göster</span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={pagination.limit}
              onChange={handlePageSizeChange}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={filters.is_active}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, is_active: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Tümü</option>
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </select>
            <form className="navbar-search">
              <input
                type="text"
                className="bg-base h-40-px w-auto"
                name="search"
                placeholder="Ara (Başlık, İçerik)..."
                value={filters.search}
                onChange={handleSearch}
              />
            </form>
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>
            <Icon icon="mdi:plus" className="me-2" />
            Yeni Sözleşme
          </button>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : contracts.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Sözleşme bulunamadı</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="bg-light">
                  <tr>
                    <th>S.L</th>
                    <th>Başlık</th>
                    <th>Versiyon</th>
                    <th>Durum</th>
                    <th>Kabul Gerektirir</th>
                    <th>Oluşturulma</th>
                    <th>Güncelleme</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((contract, index) => (
                    <tr key={contract.id}>
                      <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                      <td>{contract.title}</td>
                      <td>{contract.version}</td>
                      <td>
                        {contract.is_active ? (
                          <span className="badge bg-success">Aktif</span>
                        ) : (
                          <span className="badge bg-secondary">Pasif</span>
                        )}
                      </td>
                      <td>
                        {contract.requires_acceptance ? (
                          <span className="badge bg-info">Evet</span>
                        ) : (
                          <span className="badge bg-secondary">Hayır</span>
                        )}
                      </td>
                      <td>{formatDate(contract.created_at)}</td>
                      <td>{formatDate(contract.updated_at)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEdit(contract)}
                            title="Düzenle"
                          >
                            <Icon icon="mdi:pencil" />
                          </button>
                          <button
                            className={`btn btn-sm ${contract.is_active ? 'btn-warning' : 'btn-success'}`}
                            onClick={() => handleToggleStatus(contract.id, contract.is_active)}
                            title={contract.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                          >
                            <Icon icon={contract.is_active ? 'mdi:toggle-switch-off' : 'mdi:toggle-switch'} />
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(contract.id)}
                            title="Sil"
                          >
                            <Icon icon="mdi:delete" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="card-footer border-top bg-base py-16 px-24">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <span className="text-sm text-secondary-light">
                  Toplam {pagination.total} sözleşme, Sayfa {pagination.page} / {pagination.totalPages}
                </span>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Önceki
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Sonraki
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Sözleşme Düzenle</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedContract(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Başlık</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Versiyon</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.version}
                    onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">İçerik</label>
                  <textarea
                    className="form-control"
                    rows="10"
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    <label className="form-check-label">Aktif</label>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.requires_acceptance}
                      onChange={(e) => setFormData(prev => ({ ...prev, requires_acceptance: e.target.checked }))}
                    />
                    <label className="form-check-label">Kabul Gerektirir</label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedContract(null);
                  }}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Yeni Sözleşme</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateModal(false);
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Başlık</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Versiyon</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.version}
                    onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">İçerik</label>
                  <textarea
                    className="form-control"
                    rows="10"
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    <label className="form-check-label">Aktif</label>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.requires_acceptance}
                      onChange={(e) => setFormData(prev => ({ ...prev, requires_acceptance: e.target.checked }))}
                    />
                    <label className="form-check-label">Kabul Gerektirir</label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                  }}
                >
                  İptal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Oluştur
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default ContractsPage;

